<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { downloadFile } from '@chatwoot/utils';
import ForwardMessageModal from 'dashboard/components/widgets/conversation/ForwardMessageModal.vue';
import ContactAPI from 'dashboard/api/contacts';

// Tipos de documento oferecidos ao anexar mídia ao card do CRM. O valor é
// "slugificado" para virar o nome do arquivo (ex: "CERTIDAO DE CASAMENTO" ->
// certidao_de_casamento.pdf). O agente também pode digitar um nome livre.
const DOCUMENT_TYPES = [
  'RG',
  'CNH',
  'CERTIDAO DE CASAMENTO',
  'CERTIDAO DE NASCIMENTO',
  'TITULO DE ELEITOR',
  'CARTEIRA DE TRABALHO',
  'HOLERITE',
  'CONTRACHEQUE',
  'COMPROVANTE DE RESIDENCIA',
  'EXTRATO BANCARIO',
  'IMPOSTO DE RENDA',
];

export default {
  components: {
    AddCannedModal,
    ContextMenu,
    NextButton,
    Icon,
    ForwardMessageModal,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showForwardModal: false,
      showDocTypeModal: false,
      crmCards: [],
      isLoadingCrmCards: false,
      isAttachingToCrm: false,
      documentTypes: DOCUMENT_TYPES,
      docType: '',
      customDocName: '',
      docComplement: '',
      selectedCardId: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
      currentChat: 'getSelectedChat',
    }),
    conversationContactId() {
      return this.currentChat?.meta?.sender?.id || null;
    },
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    hasMediaAttachment() {
      const attachments = this.message.attachments || [];
      return attachments.length > 0;
    },
    firstAttachment() {
      const attachments = this.message.attachments || [];
      return attachments[0] || null;
    },
    selectedCard() {
      return this.crmCards.find(c => c.id === this.selectedCardId) || null;
    },
    documentBaseName() {
      const raw =
        this.docType === '__custom__' ? this.customDocName : this.docType;
      const base = this.slugify(raw);
      if (!base) return '';
      const comp = this.slugify(this.docComplement);
      return comp ? `${base}_${comp}` : base;
    },
    previewExtension() {
      const ext = this.firstAttachment?.extension || '';
      return ext ? ext.toString().toLowerCase() : 'ext';
    },
    previewFileName() {
      return `${this.documentBaseName}.${this.previewExtension}`;
    },
    canConfirmAttach() {
      return (
        !!this.documentBaseName &&
        !!this.selectedCardId &&
        !this.isAttachingToCrm
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    async handleDownload() {
      if (!this.firstAttachment) return;
      const { dataUrl, fileType, extension } = this.firstAttachment;
      try {
        await downloadFile({ url: dataUrl, type: fileType, extension });
      } catch {
        useAlert(this.$t('GALLERY_VIEW.ERROR_DOWNLOADING'));
      }
      this.handleClose();
    },
    handleForward() {
      this.handleClose();
      this.showForwardModal = true;
    },
    closeForwardModal() {
      this.showForwardModal = false;
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    slugify(value) {
      return (value || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    },
    async handleAttachToCrm() {
      this.handleClose();
      if (!this.conversationContactId) return;
      this.isLoadingCrmCards = true;
      try {
        const { data } = await ContactAPI.getFunnelCards(
          this.conversationContactId
        );
        this.crmCards = Array.isArray(data) ? data : [];
      } catch (e) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_LOAD_ERROR'));
        this.isLoadingCrmCards = false;
        return;
      }
      this.isLoadingCrmCards = false;

      if (this.crmCards.length === 0) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_NO_CARDS'));
        return;
      }
      // Abre o modal para escolher o tipo de documento (+ o card, se houver vários)
      this.docType = '';
      this.customDocName = '';
      this.docComplement = '';
      this.selectedCardId =
        this.crmCards.length === 1 ? this.crmCards[0].id : null;
      this.showDocTypeModal = true;
    },
    async confirmAttach() {
      if (!this.canConfirmAttach) return;
      await this.attachToCard(this.selectedCard, this.documentBaseName);
    },
    async attachToCard(card, documentName) {
      if (!card) return;
      this.isAttachingToCrm = true;
      try {
        await this.$store.dispatch('funnels/attachMessageToCard', {
          funnelId: card.funnel_id,
          cardId: card.id,
          messageId: this.messageId,
          documentName,
        });
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_SUCCESS'));
        this.showDocTypeModal = false;
      } catch (e) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_ERROR'));
      }
      this.isAttachingToCrm = false;
    },
    closeDocTypeModal() {
      this.showDocTypeModal = false;
    },
    cardLabel(card) {
      return `${card.title} — ${card.funnel_name} / ${card.stage_name}`;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="wa-menu">
        <!-- Reply -->
        <button
          v-if="enabledOptions['replyTo']"
          class="wa-menu-item"
          @mousedown.stop.prevent="handleReplyTo"
        >
          <Icon icon="i-lucide-reply" class="wa-menu-icon" />
          <span>{{ $t('CONVERSATION.CONTEXT_MENU.REPLY') }}</span>
        </button>
        <!-- Copy -->
        <button
          v-if="enabledOptions['copy']"
          class="wa-menu-item"
          @mousedown.stop.prevent="handleCopy"
        >
          <Icon icon="i-lucide-copy" class="wa-menu-icon" />
          <span>{{ $t('CONVERSATION.CONTEXT_MENU.COPY') }}</span>
        </button>
        <!-- Translate -->
        <button
          v-if="enabledOptions['translate']"
          class="wa-menu-item"
          @mousedown.stop.prevent="handleTranslate"
        >
          <Icon icon="i-lucide-languages" class="wa-menu-icon" />
          <span>{{ $t('CONVERSATION.CONTEXT_MENU.TRANSLATE') }}</span>
        </button>
        <!-- Forward -->
        <button class="wa-menu-item" @mousedown.stop.prevent="handleForward">
          <Icon icon="i-lucide-forward" class="wa-menu-icon" />
          <span>{{ $t('CONVERSATION.CONTEXT_MENU.FORWARD') }}</span>
        </button>
        <!-- Copy Permalink -->
        <button
          v-if="enabledOptions['copyLink']"
          class="wa-menu-item"
          @mousedown.stop.prevent="copyLinkToMessage"
        >
          <Icon icon="i-lucide-link" class="wa-menu-icon" />
          <span>{{ $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK') }}</span>
        </button>
        <!-- Download (media only) -->
        <template v-if="hasMediaAttachment">
          <div class="wa-menu-divider" />
          <button class="wa-menu-item" @mousedown.stop.prevent="handleDownload">
            <Icon icon="i-lucide-download" class="wa-menu-icon" />
            <span>{{ $t('CONVERSATION.CONTEXT_MENU.DOWNLOAD') }}</span>
          </button>
          <!-- Attach to CRM card -->
          <button
            class="wa-menu-item"
            :disabled="isLoadingCrmCards || isAttachingToCrm"
            @mousedown.stop.prevent="handleAttachToCrm"
          >
            <Icon icon="i-lucide-paperclip" class="wa-menu-icon" />
            <span>{{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM') }}</span>
          </button>
        </template>
        <!-- Canned Response -->
        <button
          v-if="enabledOptions['cannedResponse']"
          class="wa-menu-item"
          @mousedown.stop.prevent="showCannedResponseModal"
        >
          <Icon icon="i-lucide-bookmark-plus" class="wa-menu-icon" />
          <span>{{
            $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE')
          }}</span>
        </button>
        <!-- Delete -->
        <template v-if="enabledOptions['delete']">
          <div class="wa-menu-divider" />
          <button
            class="wa-menu-item wa-menu-item--danger"
            @mousedown.stop.prevent="openDeleteModal"
          >
            <Icon icon="i-lucide-trash-2" class="wa-menu-icon" />
            <span>{{ $t('CONVERSATION.CONTEXT_MENU.DELETE') }}</span>
          </button>
        </template>
      </div>
    </ContextMenu>
    <ForwardMessageModal
      v-if="showForwardModal"
      :show="showForwardModal"
      :conversation-id="conversationId"
      :message-ids="[messageId]"
      @cancel="closeForwardModal"
      @success="closeForwardModal"
    />
    <woot-modal
      v-if="showDocTypeModal"
      v-model:show="showDocTypeModal"
      :on-close="closeDocTypeModal"
    >
      <div class="flex flex-col p-6 gap-4 min-w-[440px]">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_MODAL_TITLE') }}
        </h3>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_DOC_TYPE') }}
          <select
            v-model="docType"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
          >
            <option value="" disabled>
              {{
                $t(
                  'CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_DOC_TYPE_PLACEHOLDER'
                )
              }}
            </option>
            <option v-for="opt in documentTypes" :key="opt" :value="opt">
              {{ opt }}
            </option>
            <option value="__custom__">
              {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_DOC_TYPE_OTHER') }}
            </option>
          </select>
        </label>

        <label
          v-if="docType === '__custom__'"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CUSTOM_NAME') }}
          <input
            v-model="customDocName"
            type="text"
            :placeholder="
              $t(
                'CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CUSTOM_NAME_PLACEHOLDER'
              )
            "
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_COMPLEMENT') }}
          <input
            v-model="docComplement"
            type="text"
            :placeholder="
              $t(
                'CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_COMPLEMENT_PLACEHOLDER'
              )
            "
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
          />
        </label>

        <label
          v-if="crmCards.length > 1"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CARD') }}
          <select
            v-model="selectedCardId"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
          >
            <option :value="null" disabled>
              {{
                $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CARD_PLACEHOLDER')
              }}
            </option>
            <option v-for="card in crmCards" :key="card.id" :value="card.id">
              {{ cardLabel(card) }}
            </option>
          </select>
        </label>

        <p v-if="documentBaseName" class="text-xs text-n-slate-11">
          {{ $t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_PREVIEW') }}
          <span class="font-mono text-n-slate-12">{{ previewFileName }}</span>
        </p>

        <div class="flex justify-end gap-2 pt-2">
          <NextButton
            variant="faded"
            color="slate"
            :label="$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CANCEL')"
            @click="closeDocTypeModal"
          />
          <NextButton
            :label="$t('CONVERSATION.CONTEXT_MENU.ATTACH_TO_CRM_CONFIRM')"
            :is-loading="isAttachingToCrm"
            :disabled="!canConfirmAttach"
            @click="confirmAttach"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style lang="scss" scoped>
.wa-menu {
  @apply py-2 min-w-[200px] bg-n-background rounded-xl shadow-xl border border-n-weak;
}

.wa-menu-item {
  @apply flex items-center gap-3 w-full px-4 py-2 text-sm text-n-slate-12
    hover:bg-n-alpha-2 cursor-pointer transition-colors duration-100;

  &--danger {
    @apply text-n-ruby-11 hover:bg-n-ruby-2;
  }
}

.wa-menu-icon {
  @apply size-4 flex-shrink-0;
}

.wa-menu-divider {
  @apply my-1 mx-3 border-t border-n-weak;
}

.context-menu--delete-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }
  }
}
</style>
